import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import * as moment from 'moment';

import { Broker } from '@c4/core';

import { Event } from './../interfaces/event.interface';
import { CodeItem } from '../interfaces/code-item.interface';

export const TAG_QSP: string = 'c4-qsp';
export const CW_CREATOR_ID: number = 3;

const MOMENT_DATE_VALUE_FORMAT: string = 'YYYY-MM-DD';
const MOMENT_DATE_DISPLAY_FORMAT: string = 'dddd, MMMM Do, YYYY';
const MOMENT_TIME_DISPLAY_FORMAT: string = 'h:mm a';

const ICON_CLASS_UNKNOWN: string = 'fa fa-fw fa-question';

export const WEEKS_OF_THE_MONTH: Array<string> = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Last'];
export const MONTHS_OF_THE_YEAR: Array<string> = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DAYS_OF_THE_WEEK: Array<any> = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

@Injectable()
export class ProgrammingUtilsService {

  constructor(
    private broker: Broker,
    private store: Store<any>
  ) { }

  buildCreatorState(
    event: {
      eventId: number;
      deviceId: number;
    },
    codeitem: {
      type: number;
      codeitemId: number;
      command: string;
      deviceId: number;
      name: string;
      id: number;
    }
  ): string {
    let state: any = {
      eventId: event.eventId,
      deviceId: event.deviceId,
      type: codeitem.type,
      tags: TAG_QSP,
      creator: CW_CREATOR_ID,
      lastUpdate: new Date().toUTCString()
    };

    if (!codeitem.codeitemId) {
      state.createdOn = new Date().toUTCString()
    }

    switch (codeitem.type) {
      case 1: state.codeitem = this.buildCommandState(codeitem); break;
      case 2: state.codeitem = this.buildConditionState(codeitem); break;
      case 3: state.codeitem = this.buildLoopState(codeitem); break;
    }

    // console.log('creator state for event and codeitem', event, codeitem, state);
    return JSON.stringify(state);
  }

  private buildCommandState(codeitem: { command: string; deviceId: number }): any {
    // console.log('buildCommandState: building command from ', codeitem);
    return {
      command: codeitem.command,
      deviceId: codeitem.deviceId,
      codeitemId: 0
    };
  }

  private buildConditionState(condition: { name: string; deviceId: number; id: number }): any {
    return {
      condition: condition.name,
      deviceId: condition.deviceId,
      codeitemId: condition.id
    };
  }

  private buildLoopState(loop: { name: string; deviceId: number; id: number }): any {
    return {
      loop: loop.name,
      deviceId: loop.deviceId,
      codeitemId: loop.id
    };
  }

  resolveProtected(events: Event[]): Event[] {
    events.forEach(evt => {
      evt.hasProtected = this._hasProtected(evt.codeItems);
    });

    // console.log('utils: protected state resolved for', events);
    return events;
  }

  private _hasProtected(codeitems: CodeItem[]): boolean {
    let hasProtected = false;
    if (codeitems) {
      codeitems.forEach(ci => {
        ci.protected = (!this._isAuthoredByCW(ci));
        // NOTE _hasProtected has to be first because otherwise it will be skipped once a protected codeitem is found
        hasProtected = this._hasProtected(ci.codeItems) || hasProtected || ci.protected;
      });
    }

    return hasProtected;
  }

  private _isAuthoredByCW(codeitem: CodeItem): boolean {
    let state = this._parseCreatorState(codeitem) || {};
    // stopgap -- remove sometime before 2.10 release
    return (!!state && state.creator === CW_CREATOR_ID || (state.tags && state.tags.indexOf(TAG_QSP) >= 0));
  }

  private _parseCreatorState(ci: CodeItem) {
    let state = undefined;
    if (!!ci && ci.creatorState && ci.creatorState.length > 0) {
      try {
        state = JSON.parse(ci.creatorState);
      } catch (e) {
        console.error('attempt to parse invalid creator state', ci.creatorState, e);
      }
    }
    return state;
  }

  private _canEdit(codeitem: CodeItem): boolean {
    return this._isAuthoredByCW(codeitem);
  }

  deriveDevicesFromItems(items: Array<any>): Array<any> {
    let devMap = {};
    let devices = [];

    items.forEach((item: any) => {
      switch (item.type) {
        case 7:
          let parent = devMap[item.parentId];
          if (parent && parent.typeName === 'device') {
            parent.proxies = parent.proxies || [];
            parent.proxies[item.id] = item;
            if (parent.proxy === item.proxy) {
              parent.defaultProxy = item;
              parent.protocolName = parent.name;
              parent.name = item.name;
            }
          } else {
            devMap[item.id] = item;
            devices.push(item);
          }
          break;
        case 6:
        default:
          devMap[item.id] = item;
          devices.push(item);
          break;
      }
    });

    return devices;
  }

  toMoment(year: number, month: number, day: number, minutes?: number) {
    return moment([
      year,
      month - 1,
      day,
      Math.floor((minutes || 0) / 60),
      Math.floor((minutes || 0) % 60)
    ]);
  }

  toStart(m: any) {
    return {
      start_date: {
        start_year: m.year(),
        start_month: m.month() + 1,
        start_day: m.date(),
        start_period: Math.floor(m.date() / 7),
        start_weekday: m.day()
      },
      offset_minutes: m.hour() * 60 + m.minute()
    };
  }

  // TODO refactor this to take year, month, day and not be coupled to the start date (because we need this for end date too)
  // once refactored, remove the export on the const above
  toDateValue(year: number, month: number, day: number): string {
    return this.toMoment(year, month, day).format(MOMENT_DATE_VALUE_FORMAT);
  }

  toDateDisplay(year: number, month: number, day: number): string {
    return this.toMoment(year, month, day).format(MOMENT_DATE_DISPLAY_FORMAT);
  }

  toTimeDisplay(year: number, month: number, day: number, minutes: number): string {
    return this.toMoment(year, month, day, minutes).format(MOMENT_TIME_DISPLAY_FORMAT);
  }

  getValue = <T, U>(store: Store<T>, callback: (state: T) => U): U => {
    var currentValue: any;
    store.select(callback).take(1).subscribe(r => currentValue = r);
    return currentValue;
  };

  // SEE http://stackoverflow.com/questions/1436438/how-do-you-set-clear-and-toggle-a-single-bit-in-javascript
  bitTest(num, bit): boolean {
    return ((num >> bit) % 2 != 0);
  }

  bitSet(num, bit): number {
    return num | 1 << bit;
  }

  bitClear(num, bit): number {
    return num & ~(1 << bit);
  }

  bitFlip(num, bit): number {
    return this.bitTest(num, bit) ? this.bitClear(num, bit) : this.bitSet(num, bit);
  }

  static distinct(x: Object, y: Object): boolean {
    return JSON.stringify(x) === JSON.stringify(y);
  }

  static snapshot<T>(obs: Observable<T>): T {
    let val: T;
    obs.take(1).subscribe(t => val = t);
    return val;
  }

  static getPathFromRoot(route: ActivatedRoute): string {
    return route.snapshot.pathFromRoot.map(path => path.url.join('/')).join('/');
  }

  static someTrue(...args: boolean[]): boolean {
    return [].slice.apply(arguments).some(p => p);
  }

  static allTrue(...arg: boolean[]): boolean {
    return [].slice.apply(arguments).some(p => p);
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/common/services/utils.service.ts